import { Element, PlanetFilterInput } from '@apeiron/planet'
import { CloseButton } from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import SkillElementButton from '@src/components/planet/filter/skillElement/SkillElementButton'
import BlurForm from '@src/components/share/filter/BlurForm'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle
} from '@src/components/share/filter/FilterSection'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import { useElementContext } from '@src/contexts/planet/ElementContext'
import * as R from 'ramda'
import { FC, Fragment, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding-left: 24px;
    padding-right: 10px;
    padding-top: 8px;
    padding-bottom: 14px;
    overflow: hidden;
  }
`

const Form = styled(BlurForm)`
  > * + * {
    margin-top: 16px;
  }
`

const ButtonsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  /* align-items: center; */
  flex-grow: 1;
`

const SelectedZone = styled(Box)`
  display: flex;
  margin-top: 8px;
  padding: 8px;
  background: #f7f7f7;
  justify-content: space-around;
`

const StyledCloseButton = styled(CloseButton)`
  height: 34px;
  width: 34px;
  flex-grow: 0;
  min-width: 40px;

  svg {
    color: #262829;
  }
`

const Header = styled(Typography)`
  font-size: 16px;
  color: #333333;
  margin-bottom: 2px;
  font-weight: 300;
`

const EmptyMessage = styled(Typography)`
  color: #565857;
  font-size: 14px;
  font-weight: 300;
`

const MAX_CSKILL_COUNT = 3

const MAX_PSKILL_COUNT = 2

const SubSection = ({
  header,
  elements,
  handleAdd,
  skillCombo,
  handleRemove,
  handleReset,
  skillLimit
}: SubSectionProps) => {
  const { t } = useTranslation()

  const selectedCount = R.sum(Object.values(skillCombo ?? {}).map(Number))

  return (
    <Box>
      <Header>{header}</Header>
      <ButtonsContainer>
        {elements.map((element: Element) => {
          return (
            <SkillElementButton
              key={element.value}
              iconUrl={element.image}
              label={element.name}
              disabled={selectedCount >= skillLimit}
              onClick={() => handleAdd(element.value as any)}
            />
          )
        })}
      </ButtonsContainer>
      <SelectedZone>
        <EmptyMessage>
          {selectedCount === 0 &&
            t('filter.sections.birth_skill_elements.empty')}
        </EmptyMessage>

        <ButtonsContainer>
          {skillCombo &&
            elements.map((element: Element) => {
              const count = skillCombo[element.value]

              return (
                <Fragment key={element.value}>
                  {!!count &&
                    Array.from({ length: count }).map((_, index) => {
                      const key = element.value + index

                      return (
                        <SkillElementButton
                          key={key}
                          iconUrl={element.image}
                          label={element.name}
                          isSelected
                          onRemove={() => handleRemove(element.value as any)}
                        />
                      )
                    })}
                </Fragment>
              )
            })}
        </ButtonsContainer>
        {selectedCount > 0 && <StyledCloseButton onClick={handleReset} />}
      </SelectedZone>
    </Box>
  )
}

const PlanetBirthSkillElementFilter: FC<Props> = (props: Props) => {
  const { className, filterOptions, onFilterOptionsChange } = props

  const { elements } = useElementContext()

  const { t } = useTranslation()

  const addElement = useCallback(
    (skillKey: 'pSkillReqElem' | 'cSkillReqElem', elementType: ElementType) => {
      const original = filterOptions[skillKey]?.[elementType] ?? 0

      onFilterOptionsChange({
        ...filterOptions,
        [skillKey]: {
          ...filterOptions[skillKey],
          [elementType]: original + 1
        }
      })
    },
    [filterOptions, onFilterOptionsChange]
  )

  const handleAddCSkillElement = useCallback(
    (elementType: ElementType) => {
      addElement('cSkillReqElem', elementType)
    },
    [addElement]
  )

  const handleAddPSkillElement = useCallback(
    (elementType: ElementType) => {
      addElement('pSkillReqElem', elementType)
    },
    [addElement]
  )

  const removeElement = useCallback(
    (skillKey: 'pSkillReqElem' | 'cSkillReqElem', elementType: ElementType) => {
      const original = filterOptions[skillKey]?.[elementType] ?? 0

      if (original === 0) {
        return
      }

      const newValue = original - 1 === 0 ? undefined : original - 1

      onFilterOptionsChange({
        ...filterOptions,
        [skillKey]: {
          ...filterOptions[skillKey],
          [elementType]: newValue
        }
      })
    },
    [filterOptions, onFilterOptionsChange]
  )

  const handleRemoveCSkillElement = useCallback(
    (elementType: ElementType) => {
      removeElement('cSkillReqElem', elementType)
    },
    [removeElement]
  )

  const handleRemovePSkillElement = useCallback(
    (elementType: ElementType) => {
      removeElement('pSkillReqElem', elementType)
    },
    [removeElement]
  )

  const resetSkill = useCallback(
    (skillType: 'pSkillReqElem' | 'cSkillReqElem') => {
      onFilterOptionsChange({
        ...filterOptions,
        [skillType]: {}
      })
    },
    [filterOptions, onFilterOptionsChange]
  )

  const handleResetPSkillElement = useCallback(() => {
    resetSkill('pSkillReqElem')
  }, [resetSkill])

  const handleResetCSkillElement = useCallback(() => {
    resetSkill('cSkillReqElem')
  }, [resetSkill])

  const handleResetAll = useCallback(() => {
    onFilterOptionsChange({
      ...filterOptions,
      cSkillReqElem: {},
      pSkillReqElem: {}
    })
  }, [filterOptions, onFilterOptionsChange])

  const numFilters = R.sum([
    R.sum(Object.values(filterOptions.cSkillReqElem ?? {}).map(Number)),
    R.sum(Object.values(filterOptions.pSkillReqElem ?? {}).map(Number))
  ])

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>
          {t('filter.sections.birth_skill_elements.title')}
        </FilterSectionTitle>
        <ResetFilter filterCount={numFilters} onReset={handleResetAll} />
      </FilterSectionHeader>
      <Content>
        <Form>
          <SubSection
            header={t('filter.sections.skill.card')}
            elements={elements}
            handleAdd={handleAddCSkillElement}
            skillCombo={filterOptions.cSkillReqElem}
            handleRemove={handleRemoveCSkillElement}
            handleReset={handleResetCSkillElement}
            skillLimit={MAX_CSKILL_COUNT}
          />
          <SubSection
            header={t('filter.sections.skill.perk')}
            elements={elements}
            handleAdd={handleAddPSkillElement}
            skillCombo={filterOptions.pSkillReqElem}
            handleRemove={handleRemovePSkillElement}
            handleReset={handleResetPSkillElement}
            skillLimit={MAX_PSKILL_COUNT}
          />
        </Form>
      </Content>
    </FilterSection>
  )
}

type ElementType = 'fire' | 'earth' | 'water' | 'air'

type Props = {
  className?: string
  filterOptions: PlanetFilterInput
  onFilterOptionsChange: (input: PlanetFilterInput) => void
}

type SubSectionProps = {
  header: string
  elements: Element[]
  handleAdd: (elementType: ElementType) => void
  skillCombo: Record<string, number | undefined> | undefined
  handleRemove: (elementType: ElementType) => void
  handleReset: () => void
  skillLimit: number
}

export default PlanetBirthSkillElementFilter
